import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Grid, TextField, Typography, FormHelperText } from '@mui/material';
import BirthdateInput from './BirthdateInput';

/**
 * PersonalInfo component
 * Collects personal information including first name, last name, email, and birthdate
 * Includes self-validation capability
 * 
 * @param {Object} props Component props
 * @param {Function} props.onFirstNameChange Callback for first name changes
 * @param {Function} props.onLastNameChange Callback for last name changes 
 * @param {Function} props.onEmailChange Callback for email changes
 * @param {Function} props.onBirthdateChange Callback for birthdate changes
 * @param {string} props.firstName Current first name value
 * @param {string} props.lastName Current last name value
 * @param {string} props.email Current email value
 * @param {Date|null} props.birthdate Current birthdate value
 * @param {boolean} props.disabled Whether the form fields should be disabled
 * @param {number} props.minAge Minimum age required (passed to BirthdateInput)
 * @param {string} props.instructionText Optional instruction text to display
 * @param {Function} props.onValidationChange Optional callback for validation state changes
 */
const PersonalInfo = forwardRef(({
  onFirstNameChange,
  onLastNameChange,
  onEmailChange,
  onBirthdateChange,
  firstName = '',
  lastName = '',
  email = '',
  birthdate = null,
  disabled = false,
  minAge = 18,
  instructionText = null,
  onValidationChange = null,
  showValidationErrors = false
}, ref) => {
  // Error states
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [birthdateError, setBirthdateError] = useState('');
  
  // Expose validation method via ref
  useImperativeHandle(ref, () => ({
    validate: () => validate(),
    isValid: () => {
      const validationError = validate(false);
      return validationError === null;
    }
  }));
  // Validate all fields
  const validate = useCallback((setErrors = true) => {
    // Reset errors
    if (setErrors && showValidationErrors) {
      setFirstNameError('');
      setLastNameError('');
      setEmailError('');
      setBirthdateError('');
    }
    
    // First name validation
    if (!firstName.trim()) {
      if (setErrors && showValidationErrors) setFirstNameError('Please enter your first name');
      return 'Please enter your first name';
    }
    
    // Last name validation
    if (!lastName.trim()) {
      if (setErrors && showValidationErrors) setLastNameError('Please enter your last name');
      return 'Please enter your last name';
    }
      // Email validation
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      if (setErrors && showValidationErrors) setEmailError('Please enter a valid email address');
      return 'Please enter a valid email address';
    }
    
    // Birthdate validation
    if (!birthdate) {
      if (setErrors && showValidationErrors) setBirthdateError('Please provide your date of birth');
      return 'Please provide your date of birth';
    }
    
    // Age validation
    const today = new Date();
    const birthDate = new Date(birthdate);
    const ageInYears = today.getFullYear() - birthDate.getFullYear();
    const hasBirthdayOccurredThisYear = 
      today.getMonth() > birthDate.getMonth() || 
      (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
    
    const calculatedAge = hasBirthdayOccurredThisYear ? ageInYears : ageInYears - 1;
    
    if (calculatedAge < minAge) {
      const error = `You must be at least ${minAge} years old to sign this waiver`;
      if (setErrors && showValidationErrors) setBirthdateError(error);
      return error;
    }
      // All validations passed
    return null;
  }, [firstName, lastName, email, birthdate, minAge, showValidationErrors]);
  
  // Validate individual fields as they change
  const validateFirstName = (value) => {
    if (!value.trim() && showValidationErrors) {
      setFirstNameError('Please enter your first name');
    } else {
      setFirstNameError('');
    }
  };
  
  const validateLastName = (value) => {
    if (!value.trim() && showValidationErrors) {
      setLastNameError('Please enter your last name');
    } else {
      setLastNameError('');
    }
  };
  
  const validateEmail = (value) => {
    if ((!value.trim() || !/\S+@\S+\.\S+/.test(value)) && showValidationErrors) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };  // Run validation when component updates
  useEffect(() => {
    if (onValidationChange) {
      const isValid = validate(false) === null;
      onValidationChange(isValid);
    }
    
    // Only update error messages if we're showing validation errors
    if (showValidationErrors) {
      validate();
    }
  }, [firstName, lastName, email, birthdate, showValidationErrors, onValidationChange, validate]);
  
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          label="First Name"
          variant="outlined"
          fullWidth
          required
          value={firstName}
          onChange={(e) => {
            const newValue = e.target.value;
            onFirstNameChange(newValue);
            validateFirstName(newValue);
          }}
          onBlur={() => validateFirstName(firstName)}
          disabled={disabled}
          inputProps={{ maxLength: 50 }}
          error={!!firstNameError}
          helperText={firstNameError}
        />
      </Grid>
      
      <Grid item xs={12} sm={6}>
        <TextField
          label="Last Name"
          variant="outlined"
          fullWidth
          required
          value={lastName}
          onChange={(e) => {
            const newValue = e.target.value;
            onLastNameChange(newValue);
            validateLastName(newValue);
          }}
          onBlur={() => validateLastName(lastName)}
          disabled={disabled}
          inputProps={{ maxLength: 50 }}
          error={!!lastNameError}
          helperText={lastNameError}
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          label="Email Address"
          variant="outlined"
          fullWidth
          required
          type="email"
          value={email}
          onChange={(e) => {
            const newValue = e.target.value;
            onEmailChange(newValue);
            validateEmail(newValue);
          }}
          onBlur={() => validateEmail(email)}
          disabled={disabled}
          inputProps={{ maxLength: 100 }}
          error={!!emailError}
          helperText={emailError}
        />
      </Grid>
      
      <Grid item xs={12}>
        <BirthdateInput
          onBirthdateChange={(date) => {
            onBirthdateChange(date);
            setBirthdateError('');
          }}
          label="Date of Birth"
          required={true}
          minAge={minAge}
          value={birthdate}
          disabled={disabled}
          error={!!birthdateError}
        />
        {birthdateError && (
          <FormHelperText error>{birthdateError}</FormHelperText>
        )}
      </Grid>
      
      {instructionText && (
        <Grid item xs={12}>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            {instructionText}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
});

export default PersonalInfo;